import { io, Socket } from 'socket.io-client';

type NoteSocketEvent =
  | 'connect'
  | 'disconnect'
  | 'connect_error'
  | 'collaboration-error'
  | 'note-user-joined'
  | 'note-user-left'
  | 'note-collaboration-state'
  | 'note-cursor-moved'
  | 'note-content-changed'
  | 'note-comment-added'
  | 'note-comment-resolved'
  | 'notes-list-changed';

type NoteSocketHandler<T = any> = (payload: T) => void;

interface NoteContentChangedPayload {
  title?: string;
  content_text?: string;
  content_json?: Record<string, any>;
  version?: number;
}

const SOCKET_EVENTS: NoteSocketEvent[] = [
  'connect',
  'disconnect',
  'connect_error',
  'collaboration-error',
  'note-user-joined',
  'note-user-left',
  'note-collaboration-state',
  'note-cursor-moved',
  'note-content-changed',
  'note-comment-added',
  'note-comment-resolved',
  'notes-list-changed',
];

function getSocketAuthToken(): string | null {
  const keycloakToken = (window as any)?.keycloak?.token;
  if (typeof keycloakToken === 'string' && keycloakToken.length > 0) {
    return keycloakToken;
  }

  const localCandidates = [
    localStorage.getItem('webelieve_access_token'),
    localStorage.getItem('token'),
    localStorage.getItem('access_token'),
  ];
  const sessionCandidates = [
    sessionStorage.getItem('webelieve_access_token'),
    sessionStorage.getItem('token'),
    sessionStorage.getItem('access_token'),
  ];
  const token = [...localCandidates, ...sessionCandidates].find((value) => typeof value === 'string' && value.length > 0);
  return token || null;
}

class NoteCollaborationSocket {
  private socket: Socket | null = null;
  private listeners = new Map<NoteSocketEvent, Set<NoteSocketHandler>>();
  private joinedNotes = new Set<string>();
  private subscribedNotes = new Set<string>();
  private connectPromise: Promise<boolean> | null = null;
  private bridgeBound = false;

  constructor() {
    SOCKET_EVENTS.forEach((event) => this.listeners.set(event, new Set()));
  }

  private emitToHandlers(event: NoteSocketEvent, payload: any): void {
    const handlers = this.listeners.get(event);
    if (!handlers || handlers.size === 0) return;
    handlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        console.error('[NotesSocket] Listener failed:', error);
      }
    });
  }

  private bindBridgeHandlers(): void {
    if (!this.socket || this.bridgeBound) return;
    SOCKET_EVENTS.forEach((event) => {
      this.socket?.on(event, (payload: any) => {
        this.emitToHandlers(event, payload);
      });
    });
    this.bridgeBound = true;
  }

  async connect(): Promise<boolean> {
    if (this.socket?.connected) return true;
    if (this.connectPromise) return this.connectPromise;

    const token = getSocketAuthToken();
    if (!token) {
      console.warn('[NotesSocket] Cannot connect: no auth token available');
      return false;
    }

    this.connectPromise = new Promise<boolean>((resolve) => {
      this.socket = io('/collaboration', {
        path: '/socket.io',
        auth: { token },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 8,
        reconnectionDelay: 1000,
      });
      this.bridgeBound = false;
      this.bindBridgeHandlers();
      this.socket.on('connect', () => {
        this.subscribedNotes.clear();
        this.joinedNotes.forEach((noteId) => {
          this.socket?.emit('join-note', { noteId });
          this.subscribedNotes.add(noteId);
        });
      });

      const finalize = (connected: boolean) => {
        this.connectPromise = null;
        resolve(connected);
      };

      this.socket.once('connect', () => {
        finalize(true);
      });

      this.socket.once('connect_error', () => {
        finalize(false);
      });
    });

    return this.connectPromise;
  }

  on<T = any>(event: NoteSocketEvent, handler: NoteSocketHandler<T>): () => void {
    const handlers = this.listeners.get(event);
    if (!handlers) return () => {};
    handlers.add(handler as NoteSocketHandler);
    return () => handlers.delete(handler as NoteSocketHandler);
  }

  async joinNote(noteId: string): Promise<void> {
    if (!noteId) return;
    this.joinedNotes.add(noteId);
    const connected = await this.connect();
    if (!connected || !this.socket) return;
    if (this.subscribedNotes.has(noteId)) return;
    this.socket.emit('join-note', { noteId });
    this.subscribedNotes.add(noteId);
  }

  leaveNote(noteId: string): void {
    if (!noteId) return;
    this.joinedNotes.delete(noteId);
    this.subscribedNotes.delete(noteId);
    if (!this.socket?.connected) return;
    this.socket.emit('leave-note', { noteId });
  }

  emitCursorMoved(noteId: string, cursorPosition: number, selectionData?: any): void {
    if (!this.socket?.connected || !noteId) return;
    this.socket.emit('note-cursor-moved', {
      noteId,
      cursor_position: cursorPosition,
      selection_data: selectionData,
    });
  }

  emitContentChanged(noteId: string, payload: NoteContentChangedPayload): void {
    if (!this.socket?.connected || !noteId) return;
    this.socket.emit('note-content-changed', {
      noteId,
      ...payload,
    });
  }

  emitCommentAdded(noteId: string, commentId?: string): void {
    if (!this.socket?.connected || !noteId) return;
    this.socket.emit('note-comment-added', {
      noteId,
      commentId,
    });
  }

  emitCommentResolved(noteId: string, commentId: string): void {
    if (!this.socket?.connected || !noteId || !commentId) return;
    this.socket.emit('note-comment-resolved', {
      noteId,
      commentId,
    });
  }

  isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }

  disconnect(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.bridgeBound = false;
    this.connectPromise = null;
    this.joinedNotes.clear();
    this.subscribedNotes.clear();
  }
}

export const noteCollaborationSocket = new NoteCollaborationSocket();
